/**
 * Copyright IBM Corp. 2022, 2026
 */

'use client'

import { useCallback, useState } from 'react'
import Image from 'next/image'
import classNames from 'classnames'
import { StandaloneLink } from '@hashicorp/mds-react/components'
import type { OnDemandResourceFieldsFragment, SiteLocale } from 'types/dato'
import PageHeadTags from 'components/page-head-tags'
import OnDemandForm from './components/on-demand-form'
import LocalizedTranslationToast from 'components/localized-translation-toast'
import { EnFallbackEventLogger } from 'app/[locale]/components/en-fallback-event-logger'
import s from './style.module.css'

export type TAppearance = 'dark' | 'light'

export interface IOndemandPageViewProps {
	isHidden?: boolean
	_locales: OnDemandResourceFieldsFragment['_locales']
	resourceDescription: string | null
	marketoFormId: number
	locale: SiteLocale
	appearance: TAppearance
	eyebrow: OnDemandResourceFieldsFragment['eyebrow']
	_seoMetaTags: OnDemandResourceFieldsFragment['_seoMetaTags']
	formTitle: OnDemandResourceFieldsFragment['formTitle']
	buttonCtaText: OnDemandResourceFieldsFragment['buttonCtaText']
	contentLength: OnDemandResourceFieldsFragment['contentLength']
	targetLink: OnDemandResourceFieldsFragment['targetLink']
	title: OnDemandResourceFieldsFragment['title']
	featuredImage: OnDemandResourceFieldsFragment['featuredImage']
}

export default function OnDemandPageView({
	_seoMetaTags,
	formTitle,
	buttonCtaText,
	contentLength,
	targetLink,
	title,
	resourceDescription,
	eyebrow,
	featuredImage,
	appearance,
	marketoFormId,
	locale,
	_locales,
}: IOndemandPageViewProps) {
	const [expanded, setExpanded] = useState(false)
	const localeNotSupportedByPage = !_locales?.includes(locale)

	const updateExpanded = useCallback(() => {
		setExpanded((prev) => !prev)
	}, [expanded])

	return (
		<>
			<PageHeadTags tags={_seoMetaTags} />
			<LocalizedTranslationToast
				onFallbackPage={localeNotSupportedByPage}
				currentLocale={locale}
				pageLocales={_locales}
			/>
			{localeNotSupportedByPage && <EnFallbackEventLogger locale={locale} />}

			<div className={s.onDemandPage} data-theme={appearance}>
				<div className={s.inner}>
					<div className={s.content}>
						<span className={s.category}>{eyebrow}</span>
						<h1 className={s.title}>{title}</h1>

						{featuredImage ? (
							<div className={s.featuredImage}>
								<Image
									src={featuredImage.url}
									alt=""
									width={featuredImage.width}
									height={featuredImage.height}
									style={{ objectFit: 'cover' }}
								/>
							</div>
						) : null}

						{/* TODO: Convert to StructuredText */}
						{resourceDescription ? (
							<>
								<div className={s.descriptionContainer}>
									<div
										className={classNames(
											s.description,
											expanded && s.expanded,
										)}
										dangerouslySetInnerHTML={{
											__html: resourceDescription,
										}}
									/>
									<div className={s.scrollGradient} />
								</div>
								<div className={s.readMoreButtonContainer}>
									<StandaloneLink
										onClick={updateExpanded}
										text={`Read ${expanded ? 'less' : 'more'}`}
										icon={expanded ? 'minus' : 'plus'}
										iconPosition="trailing"
										color="secondary"
									/>
								</div>
							</>
						) : null}
					</div>
					<div className={s.form}>
						<OnDemandForm
							appearance={appearance as 'light' | 'dark'}
							formTitle={formTitle}
							buttonCtaText={buttonCtaText}
							contentLength={contentLength}
							targetLink={targetLink}
							marketoFormId={marketoFormId}
							contentType={eyebrow.toLowerCase()}
						/>
					</div>
				</div>
			</div>
		</>
	)
}
